import { useCallback, useEffect, useRef, useState } from 'react';

export const MAP_PANEL_WIDTH_KEY = 'pratus-map-panel-width-ratio';
export const MAP_PANEL_MIN_RATIO = 0.5;
export const MAP_PANEL_MAX_RATIO = 0.75;

export const clampPanelRatio = (ratio: unknown): number => {
  const value = typeof ratio === 'number' ? ratio : Number(ratio);
  if (ratio == null || ratio === '' || !Number.isFinite(value)) {
    return MAP_PANEL_MIN_RATIO;
  }
  return Math.min(MAP_PANEL_MAX_RATIO, Math.max(MAP_PANEL_MIN_RATIO, value));
};

export const readStoredPanelRatio = (): number => {
  if (typeof window === 'undefined') return MAP_PANEL_MIN_RATIO;
  try {
    return clampPanelRatio(window.localStorage.getItem(MAP_PANEL_WIDTH_KEY));
  } catch {
    return MAP_PANEL_MIN_RATIO;
  }
};

export interface PanelWidthRatioController {
  ratio: number;
  isResizing: boolean;
  /**
   * Attach to the drag handle's `onPointerDown`; `containerEl` is the row being
   * split.
   */
  startResize: (
    event: React.PointerEvent<HTMLElement>,
    containerEl: HTMLElement | null
  ) => void;
  /** Keyboard resize for the handle (`ArrowLeft`/`ArrowRight`, `Home`). */
  onHandleKeyDown: (event: React.KeyboardEvent<HTMLElement>) => void;
}

/**
 * Left panel width as a share of the map layout, persisted to localStorage. The
 * panel is anchored to the left, so dragging its right-edge handle rightwards
 * grows it.
 */
export const usePanelWidthRatio = (): PanelWidthRatioController => {
  const [ratio, setRatio] = useState<number>(readStoredPanelRatio);
  const [isResizing, setIsResizing] = useState(false);
  const dragRef = useRef<{
    startX: number;
    startRatio: number;
    containerWidth: number;
  } | null>(null);

  useEffect(() => {
    try {
      window.localStorage.setItem(MAP_PANEL_WIDTH_KEY, String(ratio));
    } catch {
      // localStorage unavailable (private mode) — width is session-only.
    }
  }, [ratio]);

  useEffect(() => {
    if (!isResizing) return undefined;
    const onMove = (event: PointerEvent) => {
      const drag = dragRef.current;
      if (!drag) return;
      const delta = (event.clientX - drag.startX) / drag.containerWidth;
      setRatio(clampPanelRatio(drag.startRatio + delta));
    };
    const stop = () => {
      dragRef.current = null;
      setIsResizing(false);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', stop);
    window.addEventListener('pointercancel', stop);
    const prevCursor = document.body.style.cursor;
    const prevSelect = document.body.style.userSelect;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', stop);
      window.removeEventListener('pointercancel', stop);
      document.body.style.cursor = prevCursor;
      document.body.style.userSelect = prevSelect;
    };
  }, [isResizing]);

  const startResize = useCallback(
    (
      event: React.PointerEvent<HTMLElement>,
      containerEl: HTMLElement | null
    ) => {
      if (event.button !== 0 || !containerEl) return;
      const containerWidth = containerEl.getBoundingClientRect().width;
      if (!containerWidth) return;
      event.preventDefault();
      event.currentTarget.setPointerCapture?.(event.pointerId);
      dragRef.current = {
        startX: event.clientX,
        startRatio: ratio,
        containerWidth,
      };
      setIsResizing(true);
    },
    [ratio]
  );

  const onHandleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLElement>) => {
      const step = event.shiftKey ? 0.05 : 0.01;
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        setRatio((r) => clampPanelRatio(r + step));
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        setRatio((r) => clampPanelRatio(r - step));
      } else if (event.key === 'Home') {
        event.preventDefault();
        setRatio(MAP_PANEL_MIN_RATIO);
      }
    },
    []
  );

  return { ratio, isResizing, startResize, onHandleKeyDown };
};
